import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LOGO = "https://customer-assets.emergentagent.com/job_fryzjer-artysta/artifacts/1zs7ekyu_logo.png";

export default function AdminLogin() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!loading && user) return <Navigate to="/admin" replace />;

  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitting(true); setError("");
    try {
      await login(form.email, form.password);
      navigate("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Nieprawidłowy e-mail lub hasło");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-brand-base flex items-center justify-center relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <img src="https://images.unsplash.com/photo-1578598491752-1df4a1c57439?q=80&w=1600&auto=format&fit=crop"
          alt="" className="w-full h-full object-cover opacity-5" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 50%, rgba(139,0,255,0.12) 0%, transparent 70%)" }} />
      </div>

      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
        className="relative w-full max-w-md px-6">
        {/* Logo */}
        <div className="text-center mb-10">
          <img src={LOGO} alt="Fryzjer Artysta" className="h-20 w-20 object-contain mx-auto mb-4" />
          <div className="font-anton text-2xl text-white uppercase">Panel Admina</div>
          <div className="text-violet-400 text-xs uppercase tracking-[0.3em] mt-1">Fryzjer Artysta</div>
        </div>

        <div className="border border-white/8 p-8" style={{ background: "#0A0A0A" }}>
          <h2 className="font-outfit font-medium text-white mb-6">Zaloguj się</h2>
          <form data-testid="admin-login-form" onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-white/40 block mb-2">E-mail</label>
              <input data-testid="admin-email" type="email" required
                value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                className="input-dark" placeholder="admin@fryzjerartysta.pl" autoComplete="email" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-white/40 block mb-2">Hasło</label>
              <div className="relative">
                <input data-testid="admin-password" type={showPwd ? "text" : "password"} required
                  value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                  className="input-dark pr-10" placeholder="••••••••" autoComplete="current-password" />
                <button type="button" onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-0 bottom-2 text-white/30 hover:text-white transition-colors">
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            {error && <p data-testid="login-error" className="text-red-400 text-sm">{error}</p>}
            <button data-testid="admin-login-submit" type="submit" disabled={submitting}
              className="btn-primary w-full justify-center disabled:opacity-50">
              {submitting ? "Logowanie..." : "Zaloguj się"} <ArrowRight size={14} />
            </button>
          </form>
        </div>

        <p className="text-center text-white/20 text-xs mt-6 uppercase tracking-[0.15em]">
          Tylko dla administratora
        </p>
      </motion.div>
    </div>
  );
}
